import { MessagePayload } from "firebase/messaging";
import { useCallback, useMemo, useState } from "react";
import type { Dispatch, SetStateAction } from "react";

import { createElitphoneNotificationAdapterFactory } from "@/adapters/notifications";
import { NotificationSource } from "@/providers/general/notification-provider";
import { AppointmentNotification, type Call } from "@/types/call.types";
import type { IncidentReviewingNotification } from "@/types/incident.types";
import { isRecord } from "@/utils/type-guards";

import { useAppointmentNotification } from "./use-appointment-notification";
import { useCallNotification } from "./use-call-notification";
import { useIncidentNotification } from "./use-incident-notification";

const isMessagePayload = (value: unknown): value is MessagePayload => {
    return isRecord(value) && "data" in value;
};

const isMessageEvent = (value: unknown): value is MessageEvent => {
    if (typeof value !== "object" || value === null) {
        return false;
    }
    const candidate = value as { data?: unknown };
    return "data" in candidate && typeof candidate.data === "object";
};

export const extractNotificationData = (
    notification: unknown,
    context?: { source: NotificationSource }
): Record<string, unknown> | null => {
    if (context?.source === NotificationSource.FOREGROUND) {
        if (!isMessagePayload(notification)) {
            return null;
        }
        const data = isRecord(notification.data) ? notification.data : null;
        if (!data) return null;
        return data;
    }

    if (!isMessageEvent(notification)) {
        return null;
    }

    const eventData = notification.data;
    if (!eventData) {
        return null;
    }

    let extractedData: Record<string, unknown> | null = null;

    if (isRecord(eventData.payload) && isRecord(eventData.payload.data)) {
        extractedData = eventData.payload.data;
    } else if (isRecord(eventData.data)) {
        extractedData = eventData.data;
    }

    if (!extractedData) {
        return null;
    }

    return extractedData;
};

// State context for notification-related states (business logic)
export type NotificationStateContextType = {
    pendingCallsCount: number;
    pendingAppointmentNotifications: AppointmentNotification[];
    setPendingAppointmentNotifications: Dispatch<SetStateAction<AppointmentNotification[]>>;
    pendingIncidentNotifications: IncidentReviewingNotification[];
    setPendingIncidentNotifications: Dispatch<SetStateAction<IncidentReviewingNotification[]>>;
    dismissAppointmentNotification: (id: string) => void;
    dismissIncidentNotification: (incidentId: string) => void;
    pendingRecordId: string | null;
    setPendingRecordId: Dispatch<SetStateAction<string | null>>;
    pendingStaleCall: Call | null;
    confirmStaleCall: () => void;
    dismissStaleCall: () => void;
};

/**
 * Centralized notification handler hook
 * Manages all types of notifications and routes them to appropriate handlers
 */
export const useNotificationHandler = (): NotificationStateContextType & {
    notificationHandler: (notificationData: unknown, context?: { source: NotificationSource }) => void;
} => {
    const { handleCallNotification, pendingStaleCall, confirmStaleCall, dismissStaleCall } = useCallNotification();
    const {
        pendingAppointmentNotifications,
        setPendingAppointmentNotifications,
        pendingCallsCount,
        handleAppointmentStatusUpdate,
        handleAppointmentRemoval,
        dismissAppointmentNotification,
    } = useAppointmentNotification();
    const {
        pendingIncidentNotifications,
        setPendingIncidentNotifications,
        handleIncidentReviewingNotification,
        dismissIncidentNotification,
    } = useIncidentNotification();

    const [pendingRecordId, setPendingRecordId] = useState<string | null>(null);

    const adapterFactory = useMemo(
        () =>
            createElitphoneNotificationAdapterFactory({
                handleCallNotification,
                handleAppointmentStatusUpdate,
                handleAppointmentRemoval,
                handleIncidentReviewingNotification,
                setPendingRecordId,
            }),
        [
            handleCallNotification,
            handleAppointmentStatusUpdate,
            handleAppointmentRemoval,
            handleIncidentReviewingNotification,
            setPendingRecordId,
        ]
    );

    /**
     * Main notification handler that routes different types of notifications
     * to their appropriate handlers
     *
     * @param notificationData - The notification payload data
     * @param context - Optional context object indicating notification source:
     *   - NotificationSource.FOREGROUND: Notification received when app is actively open and visible ('onMessage' from firebase)
     *   - NotificationSource.SERVICE_WORKER: Notification received when app is in background/minimized
     */

    const notificationHandler = useCallback(
        (notification: unknown, context?: { source: NotificationSource }): void => {
            try {
                const notificationData = extractNotificationData(notification, context);
                if (!notificationData) {
                    console.error("Missing notification data", notification);
                    return;
                }

                const jsonPayload = notificationData.json;
                if (typeof jsonPayload !== "string") {
                    console.error("Invalid json data type", notificationData);
                    return;
                }

                const data = JSON.parse(jsonPayload);

                adapterFactory.process(data);
            } catch (error) {
                console.error("Error parsing notification data:", error);
            }
        },
        [adapterFactory]
    );

    return {
        // Notification states
        pendingCallsCount,
        pendingAppointmentNotifications,
        setPendingAppointmentNotifications,
        pendingIncidentNotifications,
        setPendingIncidentNotifications,
        dismissAppointmentNotification,
        dismissIncidentNotification,
        pendingRecordId,
        setPendingRecordId,
        pendingStaleCall,
        confirmStaleCall,
        dismissStaleCall,

        // Handler
        notificationHandler,
    };
};
